import { IonText } from "@ionic/react";
import { ReactNode, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpertAvatar from "@components/ExpertAvatar";
import ExpertRoleBoxes from "@components/ExpertRoleBoxes";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import Nbsp from "@components/Nbsp";
import TaskPopover from "@components/TaskPopover";
import styles from "@components/TaskTableRow.module.css";
import Icon from "@components/text/Icon";
import StatusText from "@components/text/StatusText";
import TextWithIcon from "@components/text/TextWithIcon";
import Heading3 from "@components/typography/Heading3";
import InternalLink from "@components/typography/InternalLink";
import useGetMyTimeWithTimezoneAbbreviation from "@hooks/useGetMyTimeWithTimezoneAbbreviation";
import useMyExpert from "@hooks/useMyExpert";
import { JourneyStatusEnum as InternalJourneyStatusEnum } from "@typing/Enums";
import { GoalStatusEnum, JourneyStatusEnum, TaskFragment as Task, TaskActorKindEnum } from "@typing/Generated";
import { getCareProfileName, showDraft, translationForTask } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { stringForDateTime } from "@utils/timeUtils";

type Props = {
  getStringForDate?: (date: Date) => ReactNode;
  handleTaskEdit: (task: Task) => void;
  handleTaskToggleCompleted: (task: Task | null) => void;
  hiddenDate: boolean;
  task: Task;
};

const TaskTableRow = ({ getStringForDate, handleTaskEdit, handleTaskToggleCompleted, hiddenDate, task }: Props) => {
  const defaultDate = new Date();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const getMyTimeWithTimezoneAbbreviation = useGetMyTimeWithTimezoneAbbreviation();

  const daysOverdue = useMemo(() => {
    const currentTime = new Date();
    const differenceInTime = currentTime.getTime() - task.dueTime.getTime();
    return Math.ceil(differenceInTime / (1000 * 3600 * 24));
  }, [task.dueTime]);

  return (
    <tr>
      <td>
        {!hiddenDate && getStringForDate && (
          <div className={styles.bigText}>
            <div className={styles.date}>{getStringForDate(task.dueTime)}</div>
          </div>
        )}
        <div className={styles.center + " " + styles.maxWidth}>
          <div className={styles.checkbox}>
            <ToggleCheckbox
              checked={task.completed}
              disabled={task.clientVisible && task.goal?.status !== GoalStatusEnum.ACTIVE && !task.completed}
              hoverContent={
                task.clientVisible
                  ? intl.formatMessage({ id: "pages.expert.tasks.cannotUpdateClientVisible" })
                  : undefined
              }
              name="markComplete"
              onChange={() => handleTaskToggleCompleted(task)}
            />
          </div>
          <IonText>
            {translationForTask(task, intl, getMyTimeWithTimezoneAbbreviation)}
            {task.actorKind === TaskActorKindEnum.AUTOMATION && (
              <IonText className={styles.autoAdded}>
                <Nbsp />
                <FormattedMessage id="pages.carePlan.autoAdded" />
              </IonText>
            )}
          </IonText>
        </div>
      </td>
      <td>
        <div className={!hiddenDate ? styles.bottom : undefined}>
          <ExpertRoleBoxes expertRoles={task.journey?.expertRoles} />
        </div>
      </td>
      <td>
        <div className={!hiddenDate ? styles.bottom : undefined}>
          {task.expert.photoUrl && <ExpertAvatar expert={task.expert} />}
        </div>
      </td>
      <td>
        <div className={`${styles.center} ${!hiddenDate ? styles.bottom : ""}`}>
          <div className={styles.spacing}>{task.client?.fullName}</div>
          {task.journey?.status === JourneyStatusEnum.INACTIVE && (
            <TextWithIcon
              color="danger"
              icon="userSlash"
              iconFirst
              text={intl.formatMessage({ id: "models.tasks.inactive" })}
            />
          )}
        </div>
      </td>
      <td>
        <div className={!hiddenDate ? styles.bottom : undefined}>
          {task.journey && (
            <InternalLink
              data-test="journey-route"
              to={journeyRoute({ journeyId: task.journey.id, linkAfterJourney: "", myExpert })}
            >
              {getCareProfileName({ intl, journey: task.journey })}
            </InternalLink>
          )}
        </div>
      </td>
      <td>
        <div className={!hiddenDate ? styles.bottom : undefined}>
          {!task.journey?.id ? null : task.clientVisible ? (
            <Icon
              color="primary-500"
              hoverContent={<FormattedMessage id="pages.expert.tasks.clientVisible" />}
              icon="eye"
            />
          ) : (
            <Icon
              color="secondary-500"
              hoverContent={<FormattedMessage id="pages.expert.tasks.clientInvisible" />}
              icon="eyeSlash"
            />
          )}
        </div>
      </td>
      <td>
        <div className={!hiddenDate ? styles.bottom : undefined}>
          <div>
            <div className={styles.overdueGroup}>
              <Heading3 bold color="gray-500" noMargin>
                {stringForDateTime(task.dueTime, intl)}
              </Heading3>
              {defaultDate > task.dueTime && !task.completed && (
                <Heading3 bold color="danger-500" noMargin>
                  <Nbsp />
                  <Nbsp />
                  <Icon color="danger" icon="alarmClock" size="small" />
                  <Nbsp />
                  <FormattedMessage id="pages.expert.tasks.daysOverdue" values={{ days: daysOverdue }} />
                </Heading3>
              )}
            </div>
            {showDraft(task) && (
              <div className={styles.draft}>
                <StatusText status={InternalJourneyStatusEnum.DRAFT} />
              </div>
            )}
          </div>
        </div>
      </td>
      <td>
        <div className={!hiddenDate ? styles.bottom : undefined}>
          <TaskPopover handleEdit={handleTaskEdit} task={task} />
        </div>
      </td>
    </tr>
  );
};

export default TaskTableRow;
